/**
 * React Query configuration and setup
 *
 * This file configures the QueryClient with sensible defaults
 * and provides type-safe query key factories.
 */

import { QueryClient, type DefaultOptions } from '@tanstack/react-query';

/**
 * Default configuration for React Query
 */
const queryConfig: DefaultOptions = {
  queries: {
    // Automatically refetch when window regains focus
    // This ensures data is fresh when user returns to tab
    refetchOnWindowFocus: true,

    // Retry failed requests once
    // More retries can cause long delays for genuinely failed requests
    retry: 1,

    // Consider data stale after 5 minutes
    // Stale data will be refetched in the background
    staleTime: 5 * 60 * 1000,

    // Keep unused data in cache for 10 minutes
    // After this time, unused data is garbage collected
    gcTime: 10 * 60 * 1000,

    // Disable automatic refetching on mount by default
    // Opt-in per query if needed
    refetchOnMount: false,
  },
  mutations: {
    // Don't retry mutations by default
    // Mutations often have side effects, retrying can cause duplicates
    retry: false,
  },
};

/**
 * Create and configure QueryClient instance
 */
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

/**
 * Query keys factory for type-safe and consistent keys
 *
 * Pattern: Use nested objects to create hierarchical keys
 * Example: queryKeys.outline.byBrief('123') => ['outlines', '123']
 *
 * Benefits:
 * - Type safety: TypeScript ensures keys are correct
 * - Consistency: All queries use the same key structure
 * - Easy invalidation: Can invalidate all outline queries with queryKeys.outline.all
 */
export const queryKeys = {
  /**
   * Outline-related query keys
   */
  outline: {
    /** All outline queries */
    all: ['outlines'] as const,

    /** Outline for a specific brief */
    byBrief: (briefId: string) => ['outlines', briefId] as const,

    /** Check if outline exists for a brief */
    exists: (briefId: string) => ['outlines', briefId, 'exists'] as const,
  },

  outlineGuides: {
    all: ['outline-guides'] as const,
    list: () => ['outline-guides', 'list'] as const,
    detail: (id: string) => ['outline-guides', id] as const,
  },

  styleGuides: {
    all: ['style-guides'] as const,
    list: () => ['style-guides', 'list'] as const,
    detail: (id: string) => ['style-guides', id] as const,
  },

  /**
   * Brief-related query keys
   */
  brief: {
    /** All brief queries */
    all: ['briefs'] as const,

    /** List of briefs (with optional filters) */
    list: (filters?: Record<string, unknown>) =>
      ['briefs', 'list', filters] as const,

    /** Single brief detail */
    detail: (id: string) => ['briefs', id] as const,
  },

  /**
   * Article-related query keys
   */
  article: {
    /** All article queries */
    all: ['articles'] as const,

    /** Article for a specific brief */
    byBrief: (briefId: string) => ['articles', briefId] as const,

    /** Check if article exists */
    exists: (briefId: string) => ['articles', briefId, 'exists'] as const,
  },

  /**
   * Insights-related query keys
   */
  insights: {
    /** All insights queries */
    all: ['insights'] as const,

    /** Insights for a specific brief */
    byBrief: (briefId: string) => ['insights', briefId] as const,

    /** Check if insights exist for a brief */
    exists: (briefId: string) => ['insights', briefId, 'exists'] as const,
  },
} as const;

const BRIEF_BROADCAST_CHANNEL = 'briefs-updates';

type BriefsBroadcastMessage = {
  type: 'briefs_changed';
  timestamp: string;
};

function canUseBroadcastChannel(): boolean {
  return globalThis.window !== undefined && 'BroadcastChannel' in globalThis;
}

export function broadcastBriefsChanged(): void {
  if (!canUseBroadcastChannel()) {
    return;
  }

  const channel = new BroadcastChannel(BRIEF_BROADCAST_CHANNEL);
  try {
    const message: BriefsBroadcastMessage = {
      type: 'briefs_changed',
      timestamp: new Date().toISOString(),
    };
    channel.postMessage(message);
  } finally {
    channel.close();
  }
}

export function subscribeToBriefsBroadcast(onMessage: () => void): (() => void) | null {
  if (!canUseBroadcastChannel()) {
    return null;
  }

  const channel = new BroadcastChannel(BRIEF_BROADCAST_CHANNEL);
  const handler = (event: MessageEvent<BriefsBroadcastMessage>) => {
    if (event.data?.type === 'briefs_changed') {
      onMessage();
    }
  };

  channel.addEventListener('message', handler);

  return () => {
    channel.removeEventListener('message', handler);
    channel.close();
  };
}
